// Main layout with Drawer and Header
import { useAppSelector } from 'store/hooks';
import React from 'react';
import { styled, Typography } from '@mui/material';
import { isString } from 'lib/typeguards';
import { Column, ColumnProps } from 'ui/Flex';
import { MuiBreadcrumb, MuiBreadcrumbs } from 'ui/MuiBreadcrumbs';
import { useRemainingHeight } from 'lib/hooks/useRemainingHeight';

const StyledTypography = styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(2)
}));

const StyledHeaderRow = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('md')]: {
        display: 'none'
    }
}));
const StyledFullHeightContainer = styled('div')({
    height: '100%',
    width: '100%'
});

interface MainLayoutProps extends React.ComponentProps<'div'> {
    pageName?: string;
    pageDescription?: string;
    headerActions?: React.ReactElement | React.ReactNode;
    noBreadcrumbs?: boolean;
    breadcrumbs?: MuiBreadcrumb[];
    columnProps?: ColumnProps;
    noScroll?: boolean;
}

export const MainLayout: React.FC<MainLayoutProps> = ({
    children,
    pageName,
    pageDescription,
    columnProps,
    breadcrumbs,
    headerActions = null,
    noBreadcrumbs,
    noScroll
}) => {
    const tenant = useAppSelector(state => state.auth.tenant);
    const childrenColumnProps = columnProps || { align: 'stretch' };
    const [height, ref] = useRemainingHeight();
    const contentStyle = React.useMemo(() => ({ height }), [height]);
    if (noScroll) {
        return (
            <StyledFullHeightContainer>
                <StyledHeaderRow ref={ref}>
                    <Column>
                        <MuiBreadcrumbs
                            rootLabel={tenant && tenant.title}
                            items={breadcrumbs}
                            title={pageName}
                            naked={noBreadcrumbs}
                        />
                        {isString(pageDescription) && (
                            <StyledTypography gutterBottom variant="caption" color="textSecondary">
                                {pageDescription}
                            </StyledTypography>
                        )}
                    </Column>
                    {headerActions}
                </StyledHeaderRow>
                <Column flex={1} {...childrenColumnProps} id="main-layout" style={contentStyle}>
                    {children}
                </Column>
            </StyledFullHeightContainer>
        );
    }
    return (
        <Column>
            <StyledHeaderRow>
                <Column>
                    <MuiBreadcrumbs
                        rootLabel={tenant && tenant.title}
                        items={breadcrumbs}
                        title={pageName}
                        naked={noBreadcrumbs}
                    />
                    {isString(pageDescription) && (
                        <StyledTypography gutterBottom variant="caption" color="textSecondary">
                            {pageDescription}
                        </StyledTypography>
                    )}
                </Column>
                {headerActions}
            </StyledHeaderRow>
            <Column flex={1} {...childrenColumnProps} id="main-layout">
                {children}
            </Column>
        </Column>
    );
};
